import { useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { useTranslation } from "react-i18next";

import usePartMutations from "../../data/usePartMutations";
import useWarehouseLocations from "../../data/useWarehouseLocations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { IPart } from "../../models/Part";
import ComboBoxInput from "../shared/Inputs/ComboBoxInput";

export default function WarehouseLocationDropdown({ part, classes = "" }: { part: IPart | undefined | null, classes?: string }) {
  const { t } = useTranslation("stock");
  const queryClient = useQueryClient();
  const { warehouseLocations } = useWarehouseLocations();
  const { patchPart } = usePartMutations();
  const { saveHandlers } = useDefaultCRUDHandlers(t("Warehouse Location"));
  const pendingClearedValueRef = useRef<boolean>(false);
  const locationOptions = [{ id: "", displayName: t("No Location") }, ...(warehouseLocations.data?.data ?? [])];

  const handleLocationUpdate = (locationId: string) => {

    if (!part) return;

    const updatedPart: any = {
      id: part.id,
      warehouseLocationId: locationId ? locationId : null,
      updateWarehouseLocation: true,
    };

    patchPart.mutate(updatedPart, {
      onSuccess: () => {
        queryClient.invalidateQueries(["pagedTable"]);
        saveHandlers.onSuccess();
      },
      onError: () => {
        saveHandlers.onError();
      }
    });
  };

  const handleSetValue = (_registerName: string, value: string) => {
    if (!part) return;

    const nextLocationId = value ? value : null;
    if (part.warehouseLocationId === nextLocationId) {
      pendingClearedValueRef.current = false;
      return;
    }

    // Clearing should be committed only when focus leaves the control.
    if (nextLocationId === null) {
      pendingClearedValueRef.current = true;
      return;
    }

    pendingClearedValueRef.current = false;
    handleLocationUpdate(value);
  };

  const handleBlurCapture = () => {
    if (!part) return;
    if (!pendingClearedValueRef.current) return;
    if (part.warehouseLocationId === null) {
      pendingClearedValueRef.current = false;
      return;
    }

    pendingClearedValueRef.current = false;
    handleLocationUpdate("");
  };

  if (warehouseLocations.isLoading || !part) {
    return <></>;
  }

  return (
    <div className="w-full h-full flex items-center" onClick={(e) => e.stopPropagation()} onBlurCapture={handleBlurCapture}>
      <ComboBoxInput
        key={`part-warehouse-location-${part.id}-${part.warehouseLocationId ?? "none"}`}
        options={locationOptions}
        searchField={"displayName"}
        valueField={"id"}
        registerName={"warehouseLocationId"}
        setValue={handleSetValue}
        register={null}
        initialValue={part.warehouseLocationId ?? ""}
        placeholder={t("Search for a Warehouse Location")}
        classes={classes}
        containerClasses="w-full"
      />
    </div>
  );
}
